import { useEffect, useState } from 'react'

/** The flow's desktop layout: the type scale and the panel's own layers switch at 1024px. */
export const DESKTOP_QUERY = '(min-width: 1024px)'

/**
 * Where the photo panel shows beside the step: from a tablet's 768px up. Narrower, the step has the
 * whole width and an open chat the whole screen.
 */
export const PANEL_QUERY = '(min-width: 768px)'

export const isDesktopViewport = () => (
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DESKTOP_QUERY).matches
)

/** Whether the photo panel shows beside the step (PANEL_QUERY). */
export const isPanelViewport = () => (
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(PANEL_QUERY).matches
)

const matchesNow = (query) => (
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(query).matches
)

/** Whether a media query matches, following the window as it changes. */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => matchesNow(query))

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const list = window.matchMedia(query)
    const update = () => setMatches(list.matches)
    update()
    list.addEventListener?.('change', update)
    return () => list.removeEventListener?.('change', update)
  }, [query])

  return matches
}

/** Whether the desktop layout applies, following the window as it is resized. */
export const useIsDesktop = () => useMediaQuery(DESKTOP_QUERY)

/** Whether the photo panel shows beside the step, following the window as it is resized. */
export const useHasPanel = () => useMediaQuery(PANEL_QUERY)

const FIELD = /^(INPUT|SELECT|TEXTAREA)$/

/**
 * Keeps the field being typed in clear of the on-screen keyboard. When the keyboard comes up (or
 * changes height), the visible part of the page shrinks; a focused field that ends up under it,
 * or within 16px of its edge, is scrolled just into view. Browsers usually do this themselves; this
 * covers the cases they don't, such as a field focused without scrolling (ui/PromoCode).
 */
export const useFocusedFieldInView = () => {
  useEffect(() => {
    const visual = window.visualViewport
    if (!visual) return
    const reveal = () => {
      const field = document.activeElement
      if (!field || !FIELD.test(field.tagName)) return
      const rect = field.getBoundingClientRect()
      const top = visual.offsetTop
      const bottom = visual.offsetTop + visual.height
      if (rect.bottom > bottom - 16) window.scrollBy({ top: rect.bottom - bottom + 16 })
      else if (rect.top < top + 16) window.scrollBy({ top: rect.top - top - 16 })
    }
    visual.addEventListener('resize', reveal)
    return () => visual.removeEventListener('resize', reveal)
  }, [])
}
